import {
  computeEffectiveSingleInputSlices,
  computeIsSingleInputVisible,
  computeSingleInputBoundaryIndex,
  computeSingleInputSlices,
  computeSingleInputSliceWidthPx,
} from 'notebook/tables/store/logic/TableSingleInputsLogic';
import {createTableStoreSelector} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {TableEditableStore, TableEditableVolatileProps} from 'notebook/tables/store/TableStore';

import {TableDimensionsModel} from './TableDimensionsSelectors';
import {TableVisibilityModel} from './TableVisibilitySelectors';

export interface TableSingleInputsModel {
  /**
   * The column index of the first non-single-input column.
   * Returns null if there are no single input columns.
   */
  boundaryIndex: number | null;

  /**
   * Whether the single input group should be visible.
   */
  isVisible: boolean;

  /**
   * The raw slices of the displayed table. Single Inputs offset the table indices, so the main
   * table is rendered starting at `slices.start`.
   * `start` and `end` are INCLUSIVE when specified; `null` means "not sliced" on that side.
   */
  slices: {
    start: number | null;
    end: number | null;
  };

  /**
   * The slices of the table, defaulting `start` to 0 and `end` to the last column index when the
   * table is not actually sliced.
   */
  effectiveSlices: {
    start: number;
    end: number;
  };

  /**
   * The pixel width of the sliced main table (from the single input boundary to the end).
   */
  sliceWidthPx: number;

  /*
  TODO(BNCH-146929): Future properties to be added as logic is transitioned out of TableEditable:
  groupHeight: number; // From _getSingleInputGroupHeight() - remains on the component because it
                       // reads the rendered single input group height from the DOM.
  */
}

export function createBoundaryIndexSelector(
  volatilePropsSelector: (store: TableEditableStore) => TableEditableVolatileProps
): (store: TableEditableStore) => number | null {
  return createTableStoreSelector(
    {
      table: (store) => volatilePropsSelector(store).table,
    },
    ({table}) => computeSingleInputBoundaryIndex(table)
  );
}

export function createTableSingleInputsSelector(
  volatilePropsSelector: (store: TableEditableStore) => TableEditableVolatileProps,
  boundaryIndexSelector: (store: TableEditableStore) => number | null,
  tableDimensionsSelector: (store: TableEditableStore) => TableDimensionsModel,
  tableVisibilitySelector: (store: TableEditableStore) => TableVisibilityModel
): (store: TableEditableStore) => TableSingleInputsModel {
  return createTableStoreSelector(
    {
      table: (store) => volatilePropsSelector(store).table,
      boundaryIndex: boundaryIndexSelector,
      shouldShowGhostCells: (store) => tableDimensionsSelector(store).shouldShowGhostCells,
      lastColumnIndex: (store) => tableDimensionsSelector(store).lastIndex[1],
      isCollapsed: (store) => tableVisibilitySelector(store).isCollapsed,
    },
    ({table, boundaryIndex, shouldShowGhostCells, lastColumnIndex, isCollapsed}) => {
      const slices = computeSingleInputSlices(boundaryIndex);
      return {
        boundaryIndex,
        isVisible: computeIsSingleInputVisible(isCollapsed, boundaryIndex),
        slices,
        effectiveSlices: computeEffectiveSingleInputSlices(slices, lastColumnIndex),
        sliceWidthPx: computeSingleInputSliceWidthPx(table, shouldShowGhostCells, slices.start),
      };
    }
  );
}
